import React from "react";
import { CircleHelp } from "lucide-react";
import Tooltip from "./Tooltip";

export type DashboardMetricAccent =
  | "producao"
  | "montagem"
  | "entrega"
  | "bordeiro"
  | "info";

const accentClass: Record<DashboardMetricAccent, { iconWrap: string }> = {
  producao: {
    iconWrap: "bg-orange-50 text-orange-600",
  },
  montagem: {
    iconWrap: "bg-emerald-50 text-emerald-700",
  },
  entrega: {
    iconWrap: "bg-blue-50 text-blue-600",
  },
  bordeiro: {
    iconWrap: "bg-amber-50 text-amber-700",
  },
  info: {
    iconWrap: "bg-neutral-100 text-neutral-700",
  },
};

function ValueSkeleton() {
  return (
    <div
      className="h-8 w-20 max-w-full animate-pulse rounded-md bg-neutral-200 sm:h-9 sm:w-24 lg:h-10"
      aria-hidden
    />
  );
}

export interface DashboardMetricCardProps {
  accent: DashboardMetricAccent;
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  isLoading?: boolean;
  /** Use for durations (monospace + tabular numbers) */
  tabularValue?: boolean;
  /** Slightly smaller value line (e.g. names in “top” cards) */
  valueSize?: "default" | "compact";
  /** Optional help text shown via icon next to the label */
  labelTooltip?: string;
}

const DashboardMetricCard: React.FC<DashboardMetricCardProps> = ({
  accent,
  icon,
  label,
  value,
  isLoading,
  tabularValue,
  valueSize = "default",
  labelTooltip,
}) => {
  const { iconWrap } = accentClass[accent];
  const valueClass = [
    valueSize === "compact"
      ? "text-base font-bold text-neutral-900 sm:text-lg lg:text-xl"
      : "text-lg font-bold text-neutral-900 sm:text-xl lg:text-2xl",
    tabularValue ? "tabular-nums" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm sm:p-4 lg:p-6">
      <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full sm:h-11 sm:w-11 lg:h-12 lg:w-12 [&_svg]:h-6 [&_svg]:w-6 sm:[&_svg]:h-7 sm:[&_svg]:w-7 lg:[&_svg]:h-8 lg:[&_svg]:w-8 ${iconWrap}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-xs font-medium text-neutral-600 sm:text-sm">
            <span className="min-w-0 truncate">{label}</span>
            {labelTooltip ? (
              <Tooltip text={labelTooltip} wide position="top">
                <button
                  type="button"
                  className="inline-flex shrink-0 text-neutral-400 hover:text-neutral-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 rounded-full"
                  aria-label={`Ajuda: ${label}`}
                >
                  <CircleHelp className="h-3.5 w-3.5" aria-hidden />
                </button>
              </Tooltip>
            ) : null}
          </p>
          {isLoading ? (
            <div className="mt-1">
              <ValueSkeleton />
            </div>
          ) : (
            <div className={`${valueClass} min-w-0 truncate`}>{value}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardMetricCard;
